import { Link, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AppContext } from "@/AppContext";
import { askCopilot, askPrompt } from "@/Apps/AITools/askCopilot";
import Markdown from "react-markdown";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getBasicUserInfo } from "@/api/auth";
import { AppShellV8 } from "../AppShellV8";
import { agentsMarkdownComponents } from "../markdown";
import {
  clock, emptyRoom, forLabel, mergeRoom, notesToShow, ownerOf, roomIsLive, whoIsViewing,
  type LiveLine, type LiveNote, type NoteFilter, type RoomResponse, type RoomState,
} from "../liveRoom";
import "./liveRoom.css";
import { transcriptFileName, transcriptText } from "../download";
import { TranscriptActions } from "../TranscriptActions";

// Live Meeting Room (LMR-6, Specs/Live Meeting Room — Spec, Oct 27 2026). The transcript fills
// in as people talk; AI notes arrive in the side panel, each with a label and who it's for.
// Polls every 3 seconds while the meeting is live; after it ends, the room stays readable.

const POLL_MS = 3000;
const INITIALS_COLORS = ["#2563eb", "#0d9488", "#7c3aed", "#db2777", "#ea580c", "#16a34a", "#0891b2"];
const colorFor = (name: string) => INITIALS_COLORS[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % INITIALS_COLORS.length];
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

function Face({ name }: { name: string }) {
  return <span className="lm-face" style={{ background: colorFor(name) }} title={name}>{initials(name)}</span>;
}

// Defined outside LiveRoom so the room isn't rebuilt on every refresh (which would reset scrolling).
function Frame({ panel, embedded, title, children }: { panel: boolean; embedded: boolean; title: string; children: ReactNode }) {
  if (embedded) return <div className="lm-embedded">{children}</div>; // inside an archived meeting's page
  if (panel) return <div className="lm-panel">{children}</div>;
  return <AppShellV8 activeApp="meetings" breadcrumb={["Meetings", title]}>{children}</AppShellV8>;
}

// embedded: shown inside an archived meeting's Transcript tab (Kyle, Oct 28), for the live room that
// recorded it (roomId) or, for older and uploaded meetings, the stored transcript (staticRoom, no notes).
interface StaticRoom { title: string; startedAt?: string | null; lines: LiveLine[] }
export function LiveRoom({ roomId, embedded = false, staticRoom }: { roomId?: string; embedded?: boolean; staticRoom?: StaticRoom } = {}) {
  const [params] = useSearchParams();
  const id = roomId ?? (params.get("id") || "");
  // Launch Desktop's floating meeting panel (LD-2) opens the room with panel=1: no app frame, a narrow
  // window that sits on top of Zoom. The transcript and the AI notes are tabs, as on a phone.
  const panel = !embedded && params.get("panel") === "1";
  const [room, setRoom] = useState<RoomState>(() =>
    staticRoom
      ? { ...emptyRoom(), meeting: { id: "", title: staticRoom.title, status: "ended", startedAt: staticRoom.startedAt || undefined }, lines: staticRoom.lines, cursor: staticRoom.lines.length }
      : emptyRoom()
  );
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<NoteFilter>("all");
  const [tab, setTab] = useState<"transcript" | "notes">("transcript");
  const [follow, setFollow] = useState(!embedded); // an archived meeting opens at the start
  // inside a meeting page, Download/Copy go up by the page's tabs (Kyle, Oct 28: no repeated header)
  const [actionsSlot, setActionsSlot] = useState<Element | null>(null);
  useEffect(() => {
    if (embedded) setActionsSlot(document.getElementById("digest-tab-actions"));
  }, [embedded]);
  const [toast, setToast] = useState<string | null>(null);
  const [flash, setFlash] = useState<number | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;
  const scrollRef = useRef<HTMLDivElement>(null);
  const seenNotes = useRef<Set<number> | null>(null);

  const youId = whoIsViewing(room.you, room.team, getBasicUserInfo());
  const appCtx = useContext(AppContext) as any;
  const [savingTodo, setSavingTodo] = useState<number | null>(null);

  // LD-3 — the buttons on each AI note
  const addTodo = async (note: LiveNote) => {
    setSavingTodo(note.n);
    try {
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/live-meetings/${encodeURIComponent(id)}/todos`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ task: note.text, owner: ownerOf(note, room.team), noteN: note.n }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.todo) setRoom((r) => ({ ...r, todos: [...r.todos, data.todo] }));
      else setToast("Couldn't save that to-do. Try again.");
    } catch {
      setToast("Couldn't save that to-do. Try again.");
    } finally {
      setSavingTodo(null);
    }
  };
  // OT-1/OT-3 — "Draft it" / "Draft the SOP": the meeting's Paperclip agent makes it (the same agent
  // and rules as the after-meeting drafts); it goes into the meeting's brief when the meeting ends.
  const draftOf = (n: number) => (room.drafts || []).find((d) => d.noteN === n);
  const askDraft = async (note: LiveNote) => {
    try {
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/live-meetings/${encodeURIComponent(id)}/drafts`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ noteN: note.n }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.draft) setRoom((r) => ({ ...r, drafts: [...(r.drafts || []).filter((d) => d.noteN !== note.n), data.draft] }));
      else setToast((data && data.message) || "Couldn't start the draft. Try again.");
    } catch {
      setToast("Couldn't start the draft. Try again.");
    }
  };
  const [viewing, setViewing] = useState<{ name: string; text: string } | null>(null);
  const openDraft = async (draftId: string) => {
    try {
      const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/live-meetings/${encodeURIComponent(id)}/drafts/${encodeURIComponent(draftId)}`);
      const data = await res.json().catch(() => null);
      if (res.ok && data && typeof data.text === "string") setViewing({ name: data.name, text: data.text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n/, "") });
      else setToast("That draft isn't ready yet.");
    } catch {
      setToast("Couldn't open the draft. Try again.");
    }
  };
  // OT-8b: the Slack post for a ready draft links here with draft=<id>; open it once it's loaded
  const linkedDraft = params.get("draft");
  const openedLinkedDraft = useRef(false);
  useEffect(() => {
    if (!linkedDraft || openedLinkedDraft.current) return;
    if (!(room.drafts || []).some((d) => d.id === linkedDraft && d.status === "ready")) return;
    openedLinkedDraft.current = true;
    openDraft(linkedDraft);
  }, [linkedDraft, room.drafts]);
  const meetingName = room.meeting?.title || "the meeting";

  // Poll for what's new after our cursors; stop once the meeting has ended and we have it all.
  useEffect(() => {
    if (!id || staticRoom) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      const cur = roomRef.current;
      try {
        const res = await fetch(`${DOCS_API_DOMAIN}/api/agents/live-meetings/${encodeURIComponent(id)}/room?since=${cur.cursor}&notesSince=${cur.notesCursor}`);
        if (res.status === 404) {
          setError("This meeting room isn't available.");
          return;
        }
        if (!res.ok) throw new Error(String(res.status));
        const data: RoomResponse = await res.json();
        if (stop) return;
        setError(null);
        const next = mergeRoom(roomRef.current, data);
        setRoom(next);
        if (!roomIsLive(next)) return;
      } catch {
        if (!stop) setError("Reconnecting…");
      }
      if (!stop) timer = setTimeout(load, POLL_MS);
    };
    load();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [id]);

  // A toast when a note for you arrives (not for the notes already there when the room opened).
  useEffect(() => {
    if (!room.meeting) return;
    if (seenNotes.current === null) {
      seenNotes.current = new Set(room.notes.map((x) => x.n));
      return;
    }
    const fresh = room.notes.filter((x) => !seenNotes.current!.has(x.n));
    fresh.forEach((x) => seenNotes.current!.add(x.n));
    const mine = fresh.find((x) => youId && x.forId === youId);
    if (mine) {
      // in Launch Desktop, a Mac notification too when the app isn't in front (LD-6)
      const desktop = (window as any).launchDesktop;
      if (desktop?.notify && !document.hasFocus()) desktop.notify({ title: `Note for you · ${room.meeting.title}`, body: `${mine.label}: ${mine.text}` });
      setToast(`New note for you: ${mine.label}`);
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [room.notes, room.meeting, youId]);

  // Follow the newest line unless the reader scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && follow) el.scrollTop = el.scrollHeight;
  }, [room.lines.length, follow, tab]);

  const jumping = useRef(false); // "See the moment" is moving the transcript; don't read that as following the live end
  const onScroll = () => {
    const el = scrollRef.current;
    if (jumping.current) return;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (atBottom !== follow) setFollow(atBottom);
  };

  const seeMoment = (note: LiveNote) => {
    setTab("transcript");
    setFollow(false);
    setFlash(note.atLine);
    jumping.current = true;
    setTimeout(() => document.getElementById(`lm-line-${note.atLine}`)?.scrollIntoView({ block: "center" }), 50);
    setTimeout(() => (jumping.current = false), 600);
    setTimeout(() => setFlash(null), 2000);
  };

  const live = roomIsLive(room);
  const speakers = [...new Set(room.lines.map((l) => l.speaker))];
  const mineCount = youId ? room.notes.filter((x) => x.forId === youId).length : 0;
  const teamCount = room.notes.filter((x) => x.forId === null).length;
  const shown = notesToShow(room.notes, filter, youId);
  const lastSec = room.lines.length ? room.lines[room.lines.length - 1].startSec || 0 : 0;
  const title = room.meeting?.title || "Meeting";

  return (
    <Frame panel={panel} embedded={embedded} title={title}>
      <div className="lm">
        {embedded && room.lines.length > 0 &&
          (actionsSlot ? (
            createPortal(<TranscriptActions fileName={transcriptFileName(title, room.meeting?.startedAt)} text={() => transcriptText(title, room.lines)} />, actionsSlot)
          ) : (
            <div className="lm-embedded-actions">
              <TranscriptActions fileName={transcriptFileName(title, room.meeting?.startedAt)} text={() => transcriptText(title, room.lines)} />
            </div>
          ))}
        {!embedded && (
        <div className="lm-head">
          {!panel && <Link to="/agents/meetings" className="lm-crumb">Meetings ›</Link>}
          <span className="lm-title">{title}</span>
          {room.meeting && (live ? <span className="lm-live">Live</span> : <span className="lm-ended">Ended</span>)}
          {room.lines.length > 0 && lastSec > 0 && <span className="lm-elapsed">{clock(lastSec)}</span>}
          {error && <span className="lm-error">{error}</span>}
          <span className="lm-faces">{speakers.slice(0, 8).map((s) => <Face key={s} name={s} />)}</span>
          {room.lines.length > 0 && (
            <span className="lm-dl">
              {/* what's been said so far — live or after (Claire, Oct 28 scrum) */}
              <TranscriptActions fileName={transcriptFileName(title, room.meeting?.startedAt)} text={() => transcriptText(title, room.lines)} />
            </span>
          )}
        </div>
        )}
        {room.meeting && !live && !embedded && (
          <div className="lm-endbar">
            This meeting has ended. Its brief, to-dos and drafts appear in Meetings in about 10 minutes. The transcript and AI notes stay here.
          </div>
        )}
        {!staticRoom && (
          <div className="lm-tabs">
          <button aria-pressed={tab === "transcript"} onClick={() => setTab("transcript")}>Transcript</button>
          <button aria-pressed={tab === "notes"} onClick={() => setTab("notes")}>AI notes{mineCount ? ` (${mineCount} for you)` : ""}</button>
        </div>
        )}
        <div className="lm-cols" data-tab={tab} data-nonotes={staticRoom ? "1" : undefined}>
          <section className="lm-tcol">
            {!embedded && <div className="lm-colhead">Transcript</div>}
            <div className="lm-scroll" ref={scrollRef} onScroll={onScroll}>
              {!room.meeting && !error && <p className="lm-empty">Loading…</p>}
              {room.meeting && room.lines.length === 0 && <p className="lm-empty">Waiting for the first words…</p>}
              {room.lines.map((l, i) => (
                <div key={i} id={`lm-line-${i}`} className={`lm-turn ${flash === i ? "lm-flash" : ""}`}>
                  <Face name={l.speaker} />
                  <div className="lm-who">{l.speaker.split(/\s+/)[0]}{l.startSec != null && <span>{clock(l.startSec)}</span>}</div>
                  <div className="lm-txt">{l.text}</div>
                </div>
              ))}
              {live && room.lines.length > 0 && <div className="lm-listening"><i><b /><b /><b /></i> listening…</div>}
            </div>
            {!follow && (
              <button className="lm-jump" onClick={() => setFollow(true)}>{live ? "Jump to live ↓" : "Back to the end ↓"}</button>
            )}
          </section>
          {!staticRoom && (
          <aside className="lm-ncol">
            {!embedded && <div className="lm-colhead">AI notes</div>}
            <div className="lm-chips">
              <button aria-pressed={filter === "all"} onClick={() => setFilter("all")}>All<span>{room.notes.length}</span></button>
              {youId && <button aria-pressed={filter === "me"} onClick={() => setFilter("me")}>For me<span>{mineCount}</span></button>}
              <button aria-pressed={filter === "team"} onClick={() => setFilter("team")}>For the team<span>{teamCount}</span></button>
            </div>
            <div className="lm-scroll">
              <div className="lm-notes">
                {room.lookups.map((l) => (
                  <div key={l.id} className="lm-note lm-looking">
                    <div className="lm-top"><span className="lm-type lm-t-fact">Looking up</span><span className="lm-spin" /></div>
                    <p>{l.question}</p>
                  </div>
                ))}
                {shown.map((x) => {
                  const mine = !!youId && x.forId === youId;
                  return (
                    <article key={x.n} className={`lm-note ${mine ? "lm-mine" : ""}`}>
                      <div className="lm-top">
                        <span className={`lm-type lm-t-${x.type}`}>{x.label}</span>
                        <span className="lm-for">{forLabel(x, youId, room.team)}</span>
                        <span className="lm-at">{room.lines[x.atLine] ? clock(room.lines[x.atLine].startSec || 0) : ""}</span>
                      </div>
                      {x.lookedUp && x.question && <p className="lm-q">Looked up: {x.question}</p>}
                      <div className="lm-body"><Markdown components={agentsMarkdownComponents as any}>{x.text}</Markdown></div>
                      {x.lookedUp && x.source && <p className="lm-source">Source: {x.source}</p>}
                      <div className="lm-actions">
                        {room.todos.some((t) => t.noteN === x.n) ? (
                          <span className="lm-done">To-do saved ✓</span>
                        ) : (
                          <button onClick={() => addTodo(x)} disabled={savingTodo === x.n}>{savingTodo === x.n ? "Saving…" : "Add as to-do"}</button>
                        )}
                        {(() => {
                          const d = draftOf(x.n);
                          const label = x.type === "sop" ? "Draft the SOP" : "Draft it";
                          if (!d) return <button onClick={() => askDraft(x)}>{label}</button>;
                          if (d.status === "drafting") return <span className="lm-done">Drafting…</span>;
                          if (d.status === "ready") return <button onClick={() => openDraft(d.id)}>Open draft</button>;
                          return (
                            <>
                              <span className="lm-draft-error">{d.error || "The draft didn't finish."}</span>
                              <button onClick={() => askDraft(x)}>Try again</button>
                            </>
                          );
                        })()}
                        <button onClick={() => askCopilot(appCtx, askPrompt(x.text, meetingName))}>Ask CoPilot</button>
                      </div>
                      <button className="lm-moment" onClick={() => seeMoment(x)}>See the moment →</button>
                    </article>
                  );
                })}
                {room.meeting && shown.length === 0 && room.lookups.length === 0 && (
                  <p className="lm-empty">
                    {room.notes.length ? "Nothing here yet." : live ? "Listening. Notes appear only when there's something worth saying." : "No notes for this meeting."}
                  </p>
                )}
              </div>
            </div>
            <div className="lm-ask">
              <div className="lm-askbox">Ask about this meeting…</div>
              <small>Coming with the Copilot: ask a question and the answer shows here for everyone.</small>
            </div>
          </aside>
          )}
        </div>
        {toast && <div className="lm-toast" role="status">{toast}</div>}
        {viewing && (
          <div className="lm-draftview" role="dialog" aria-label={viewing.name}>
            <div className="lm-draftview-head">
              <strong>{viewing.name}</strong>
              <button onClick={() => setViewing(null)}>Close</button>
            </div>
            <p className="lm-draftview-note">A first draft from the meeting's AI. It goes into the meeting's brief for review when the meeting ends.</p>
            <div className="lm-body lm-draftview-body"><Markdown components={agentsMarkdownComponents as any}>{viewing.text}</Markdown></div>
          </div>
        )}
      </div>
    </Frame>
  );
}
